import React from 'react';
import './Radio.css';

export interface WxbRadioOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

export interface WxbRadioGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'defaultValue' | 'onChange'
> {
  options: WxbRadioOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  direction?: 'horizontal' | 'vertical';
  disabled?: boolean;
  name?: string;
}

export const WxbRadioGroup: React.FC<WxbRadioGroupProps> = ({
  options,
  value: controlledValue,
  defaultValue = '',
  onChange,
  direction = 'horizontal',
  disabled = false,
  className = '',
  name,
  'aria-label': ariaLabel = '选项',
  ...props
}) => {
  const autoName = React.useId().replace(/:/g, '');
  const radioName = name || `wxb-radio-${autoName}`;
  const [internal, setInternal] = React.useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internal;

  const handleChange = (option: WxbRadioOption, event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || option.disabled) return;
    if (!isControlled) setInternal(option.value);
    onChange?.(option.value, event);
  };

  return (
    <div
      {...props}
      className={`wxb-radio-group wxb-radio-${direction} ${className}`}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
    >
      {options.map((option) => {
        const optionDisabled = disabled || option.disabled;
        const checked = currentValue === option.value;

        return (
          <label
            key={option.value}
            className={`wxb-radio ${checked ? 'is-checked' : ''} ${optionDisabled ? 'is-disabled' : ''}`}
          >
            <input
              className="wxb-radio-input"
              type="radio"
              name={radioName}
              value={option.value}
              checked={checked}
              disabled={optionDisabled}
              onChange={(event) => handleChange(option, event)}
            />
            <span className="wxb-radio-dot" aria-hidden="true" />
            <span className="wxb-radio-label">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};
